import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import {
  Add,
  AirportShuttle,
  Attractions,
  ConfirmationNumber,
  Dashboard,
  DirectionsBoat,
  DirectionsBus,
  DirectionsCar,
  Flight,
  Hotel,
  SimCard,
  Tour,
  Train,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { routes } from '@frontend/router/routes';
import { visibleSearchEntries } from '@frontend/features/travel-services/searchNavigation';
import { useSearchAvailability } from '@frontend/features/travel-services/useSearchAvailability';
import { NavBarButton } from './styles';
import { visibleNavigationCount } from './navigationOverflow';

const icons: Record<string, typeof Flight> = {
  flights: Flight,
  hotels: Hotel,
  transfers: AirportShuttle,
  experiences: Attractions,
  cars: DirectionsCar,
  rail: Train,
  ferry: DirectionsBoat,
  circuits: Tour,
  esims: SimCard,
  buses: DirectionsBus,
  passes: ConfirmationNumber,
};
const buttonSx = { borderRadius: 100, whiteSpace: 'nowrap', flexShrink: 0 } as const;

interface Props {
  navigate: (route: string) => () => void;
  onFlightsClick: () => void;
  flightsOpen: boolean;
}

export function SearchNavigation({ navigate, onFlightsClick, flightsOpen }: Props) {
  const { ability } = useAuth();
  const availability = useSearchAvailability();
  const entries = visibleSearchEntries((permission) => Boolean(ability?.can('view', permission)), availability);
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const container = useRef<HTMLDivElement>(null);
  const measurement = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState(0);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const signature = entries.map((entry) => entry.id).join(',');
  const labels = entries.map((entry) => t(`common.nav.${entry.id}`)).join(',');

  useEffect(() => setAnchor(null), [signature, count]);

  useLayoutEffect(() => {
    const holder = container.current;
    const row = measurement.current;
    if (!holder || !row) return;
    const measure = () => {
      const children = Array.from(row.children);
      const widths = children.slice(0, -1).map((child) => child.getBoundingClientRect().width);
      const moreWidth = children[children.length - 1]?.getBoundingClientRect().width ?? 40;
      setCount(visibleNavigationCount(widths, holder.getBoundingClientRect().width, moreWidth, 4));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(holder);
    Array.from(row.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [signature, labels]);

  const isActive = (id: string, route: string) =>
    id === 'flights'
      ? flightsOpen || pathname.startsWith('/flights/')
      : id === 'hotels'
        ? pathname === route || pathname.startsWith(`${route}/`) || pathname.startsWith('/hotel-fares/')
        : pathname === route;
  const hidden = entries.slice(count);
  const close = () => setAnchor(null);
  const activate = (id: string, route: string) => {
    close();
    if (id === 'flights') onFlightsClick();
    else navigate(route)();
  };

  return (
    <Box
      component="nav"
      aria-label={t('common.nav.searchNavigation')}
      sx={{ display: 'flex', flex: 1, minWidth: 0, gap: 0.5, alignItems: 'center' }}
    >
      {ability?.can('view', 'Dashboard') && (
        <NavBarButton
          size="small"
          variant="outlined"
          sx={buttonSx}
          startIcon={<Dashboard />}
          className={pathname.startsWith(routes.dashboard.index) ? 'active' : ''}
          onClick={navigate(routes.dashboard.index)}
        >
          {t('common.nav.dashboard')}
        </NavBarButton>
      )}
      <Box ref={container} sx={{ flex: 1, minWidth: 40, position: 'relative' }}>
        <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {entries.slice(0, count).map((entry) => {
            const Icon = icons[entry.id];
            return (
              <NavBarButton
                key={entry.id}
                size="small"
                variant="outlined"
                sx={buttonSx}
                startIcon={<Icon />}
                className={isActive(entry.id, entry.route) ? 'active' : ''}
                onClick={() => activate(entry.id, entry.route)}
              >
                {t(`common.nav.${entry.id}`)}
              </NavBarButton>
            );
          })}
          {hidden.length > 0 && (
            <NavBarButton
              id="search-overflow-button"
              size="small"
              variant="outlined"
              sx={{ ...buttonSx, minWidth: 40 }}
              aria-label={t('common.nav.moreSearches')}
              aria-haspopup="menu"
              aria-expanded={Boolean(anchor)}
              aria-controls={anchor ? 'search-overflow-menu' : undefined}
              className={hidden.some((entry) => isActive(entry.id, entry.route)) ? 'active' : ''}
              onClick={(event) => setAnchor(event.currentTarget)}
            >
              <Add />
            </NavBarButton>
          )}
        </Box>
        {/* Identical intrinsic widths, outside layout and inaccessible to keyboard/AT. */}
        <Box
          ref={measurement}
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: 0,
            overflow: 'hidden',
            visibility: 'hidden',
            display: 'flex',
            width: '100%',
            pointerEvents: 'none',
          }}
        >
          {entries.map((entry) => {
            const Icon = icons[entry.id];
            return (
              <NavBarButton
                key={entry.id}
                tabIndex={-1}
                size="small"
                variant="outlined"
                sx={buttonSx}
                startIcon={<Icon />}
              >
                {t(`common.nav.${entry.id}`)}
              </NavBarButton>
            );
          })}
          <NavBarButton tabIndex={-1} size="small" variant="outlined" sx={{ ...buttonSx, minWidth: 40 }}>
            <Add />
          </NavBarButton>
        </Box>
      </Box>
      <Menu
        id="search-overflow-menu"
        anchorEl={anchor}
        open={Boolean(anchor) && hidden.length > 0}
        onClose={close}
        MenuListProps={{ 'aria-labelledby': 'search-overflow-button' }}
      >
        {hidden.map((entry) => {
          const Icon = icons[entry.id];
          return (
            <MenuItem
              key={entry.id}
              selected={isActive(entry.id, entry.route)}
              onClick={() => activate(entry.id, entry.route)}
            >
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t(`common.nav.${entry.id}`)}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
